import * as d3 from 'd3';

export default class Chart {
  async create(
    el,
    data,
    {
      primaryColour = '#666',
      highlightColour = '#333',
      meanLineColour = '#000',
      axisLabels = {},
      suffix = null,
      animate = true,
      groupAverage = null,
    },
  ) {
    this.data = data;
    this.meanLineColour = meanLineColour;
    this.axisLabels = axisLabels;
    this.eventBus = eventBus;
    this.groupAverage = groupAverage;

    // Determin label length
    if (document.fonts && document.fonts.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => {
          setTimeout(resolve, 2000);
        }),
      ]);
    }

    const labelWidth = this.getLabelWidth({
      el,
      data,
      axisLabels,
    });

    // Set chart dimensions
    const outerWidth = el.getBoundingClientRect().width;
    const scaledRange = d3.max(data, (d) => d.value);

    const margin = { top: 50, right: 100, bottom: labelWidth + 20, left: 80 },
      width = outerWidth - margin.left - margin.right,
      height = 400;

    this.margin = margin;
    this.width = width;
    this.height = height;

    this.chart = d3
      .select(el)
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .attr('viewBox', '0 0 ' + outerWidth + ' ' + (height + margin.top + margin.bottom))
      .append('g')
      .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

    const chart = this.chart;

    this.y = d3.scaleLinear().domain([0, scaledRange]).range([height, 0]);

    const barWidth = width / data.length;
    const barMargin = barWidth / 8 < 10 ? barWidth / 8 : 10;

    // Create bars
    const bar = chart
      .selectAll('g')
      .data(data)
      .enter()
      .append('g')
      .attr('transform', (d, i) => 'translate(' + i * barWidth + ',0)')
      .on('mouseover', function (d) {
        eventBus.publish({ obj: d, evt: 'mouseover' });
      })
      .on('mouseout', function (d) {
        eventBus.publish({ obj: d, evt: 'mouseout' });
      });

    const y = this.y;

    bar.each(function (d) {
      let el = d3.select(this);
      if ('link' in d) {
        el = el.append('svg:a').attr('href', d.link);
      }

      const rect = el
        .append('rect')
        .attr('class', 'bar')
        .attr('x', barMargin / 2)
        .attr('y', height)
        .attr('width', barWidth - barMargin)
        .attr('fill', (d) => {
          if ('highlight' in d && d.highlight) {
            return highlightColour;
          }
          return primaryColour;
        });

      if (animate) {
        rect
          .transition()
          .duration(800)
          .attr('y', (d) => y(d.value))
          .attr('height', (d) => height - y(d.value));
      } else {
        rect.attr('y', (d) => y(d.value)).attr('height', (d) => height - y(d.value));
      }
    });

    this.addBarLabels(barWidth, suffix, animate);
    this.leftAxis();
    this.bottomAxis();
    this.meanLine();
  }

  applyStyles(el, styles) {
    const selection = d3.select(el);
    Object.keys(styles).forEach((attr) => {
      selection.attr(attr, styles[attr]);
    });
  }

  addBarLabels(barWidth, suffix, animate) {
    const chart = this.chart;
    const eventBus = this.eventBus;

    let text = chart
      .selectAll('text')
      .data(this.data)
      .enter()
      .append('text')
      .attr('x', (d, i) => i * barWidth + barWidth / 2)
      .attr('y', (d) => this.y(d.value))
      .attr('dy', '-.25em')
      .attr('class', 'chart__label')
      .attr('text-anchor', 'middle')
      .text((d) => d.value + (suffix ? suffix : ''));

    // If we have a large amount of datapoints so that labels are overlapping
    // set labels to {visibility: hidden} and add listeners to show on hover.
    // const textNodes = text.nodes()
    // const overlap = textNodes.some(nodeA => {
    //   return textNodes.some(nodeB => {
    //     if (nodeA == nodeB) return false
    //     return doesOverlap(
    //       nodeA.getBoundingClientRect(),
    //       nodeB.getBoundingClientRect()
    //     )
    //   })
    // })

    // Alwyas perform for now, this may change in the future
    if (true) {
      const textSize = text.size();
      text.each(function (d, i) {
        if (!('highlight' in d && d.highlight) && i != 0 && i != textSize - 1) {
          d3.select(this).attr('visibility', 'hidden');

          eventBus.add({
            listener: () => {
              d3.select(this).attr('visibility', 'visible');
            },
            to: d,
            for: 'mouseover',
          });

          eventBus.add({
            listener: () => {
              d3.select(this).attr('visibility', 'hidden');
            },
            to: d,
            for: 'mouseout',
          });
        }
      });
    }

    if (animate) {
      text.style('opacity', 0).transition().delay(800).style('opacity', 1);
    }
  }

  leftAxis() {
    const chart = this.chart;
    const yAxis = d3.axisLeft(this.y);
    chart
      .append('g')
      .call(yAxis)
      .call((g) => {
        g.attr('font-size', null);
        g.attr('font-family', null);
      });

    if (this.axisLabels.y) {
      chart
        .append('text')
        .attr('x', 0)
        .attr('y', this.height / 2)
        .attr('dy', -this.margin.left)
        .attr('dominant-baseline', 'hanging')
        .attr('transform', 'rotate(-90, 0, ' + this.height / 2 + ')')
        .attr('text-anchor', 'middle')
        .attr('class', 'chart__axis-label')
        .text(this.axisLabels.y);
    }
  }

  bottomAxis() {
    const chart = this.chart;

    const x = d3
      .scaleBand()
      .domain(this.data.map((d) => (d.label && d.label ? d.label : '')))
      .range([0, this.width]);

    const xAxis = d3.axisBottom(x);

    const bottomAxis = chart
      .append('g')
      .attr('transform', 'translate(0, ' + this.height + ')')
      .call(xAxis)
      .call((g) => {
        g.attr('font-size', null);
        g.attr('font-family', null);
      });

    bottomAxis
      .selectAll('text')
      .attr('transform', 'translate(14, 14) rotate(90)')
      .attr('text-anchor', 'start');

    const axisEls = bottomAxis.selectAll('.tick');

    //const axisNodes = axisEls.nodes();

    // If we have a large amount of datapoints so that labels are overlapping
    // set labels to {visibility: hidden} and add listeners to show on hover.
    // const overlap = axisNodes.some(nodeA => {
    //   return axisNodes.some(nodeB => {
    //     if (nodeA == nodeB) return false
    //     return doesOverlap(
    //       nodeA.getBoundingClientRect(),
    //       nodeB.getBoundingClientRect()
    //     )
    //   })
    // })

    // Always perform for now, this may change in the future
    if (true) {
      const eventBus = this.eventBus;
      const data = this.data;

      axisEls.each(function (d, i) {
        const dataObj = data[i];
        if (!('highlight' in dataObj && dataObj.highlight)) {
          const d3Obj = d3.select(this);
          d3Obj.attr('visibility', 'hidden');

          eventBus.add({
            listener: () => {
              d3Obj.attr('visibility', 'visible');
            },
            to: dataObj,
            for: 'mouseover',
          });
          eventBus.add({
            listener: () => {
              d3Obj.attr('visibility', 'hidden');
            },
            to: dataObj,
            for: 'mouseout',
          });
        }
      });
    }

    const axisTicks = bottomAxis.selectAll('.tick');
    axisTicks.each((d) => {
      const p = this.data.find((i) => i.label == d);
      if ('labelLink' in p) {
        const tick = d3.select(this);
        const link = tick.append('svg:a').attr('href', p.labelLink);
        link.append(() => tick.select('text').node());
      }
    });

    if (this.axisLabels.x) {
      chart
        .append('text')
        .attr('x', this.width / 2)
        .attr('y', this.height)
        .attr('dy', this.margin.bottom)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'top')
        .attr('class', 'chart__axis-label')
        .text(this.axisLabels.x);
    }
  }

  meanLine() {
    const chart = this.chart;
    const groupMean = this.groupAverage;

    chart
      .append('line')
      .attr('x1', 0)
      .attr('y1', this.y(groupMean))
      .attr('x2', this.width)
      .attr('y2', this.y(groupMean))
      .attr('stroke-width', 2)
      .attr('stroke', this.meanLineColour);

    chart
      .append('text')
      .attr('x', this.width)
      .attr('y', this.y(groupMean))
      .attr('dx', '5')
      .attr('dy', '5')
      .text('Mean ' + groupMean);
  }

  getLabelWidth({ el, data, axisLabels }) {
    let labelWidths = data.map((i) => {
      const text = d3.select(el).append('text').text(i.label);

      const width = text.node().getBoundingClientRect().width;
      text.remove();
      return width;
    });

    let axisLabelWidth = 0;
    if (axisLabels.y) {
      axisLabelWidth = (() => {
        const text = d3.select(el).append('text').text(axisLabels.y);

        const height = text.node().getBoundingClientRect().height;
        text.remove();
        return height;
      })();
    }

    return Math.max(...labelWidths) + axisLabelWidth;
  }

  empty(el) {
    const parent = d3.select(el);
    parent.selectAll('*').remove();
  }

  roundToTwo(num) {
    return +(Math.round(num + 'e+2') + 'e-2');
  }
}

// function doesOverlap(nodeA, nodeB) {
//   return (
//     nodeA.left < nodeB.right &&
//     nodeA.right > nodeB.left &&
//     nodeA.top < nodeB.bottom &&
//     nodeA.bottom > nodeB.top
//   );
// }

const eventBus = {
  store: new Map(),
  add({ listener, to, for: forEvt }) {
    if (!this.store.has(to)) {
      this.store.set(to, new Map());
    }

    const evtListeners = this.store.get(to);

    if (!evtListeners.has(forEvt)) {
      evtListeners.set(forEvt, new Set());
    }

    const subscribers = evtListeners.get(forEvt);

    subscribers.add(listener);
  },
  publish({ obj, evt }) {
    if (this.store.has(obj)) {
      const handlers = this.store.get(obj).get(evt);
      handlers.forEach((handler) => handler());
    }
  },
};
